import Dialog from 'rc-dialog'
import React, { FC, Suspense } from 'react'
import { BankrateOffer } from '@bankrate/mortgage-types'
import currency from 'utils/currency'

import { ReactComponent as PhoneIcon } from 'assets/phone.svg'

import { useStoreState } from 'store'
import CTAButton from 'components/rate-table/row/CTAButton'
import { retry } from '../../utils/retry'

const DetailsTab = React.lazy(() => retry(() => import(/* webpackChunkName: "DetailsTab" */ './DetailsTab')))
const PaymentTab = React.lazy(() => retry(() => import(/* webpackChunkName: "PaymentTab" */ './PaymentTab')))
const ReviewsTab = React.lazy(() => retry(() => import(/* webpackChunkName: "ReviewsTab" */ './ReviewsTab')))

export enum Tab {
  Details = 'details',
  Payment = 'payment',
  Reviews = 'reviews',
}

export interface Props {
  visible: boolean
  offer: BankrateOffer
  setVisible: React.Dispatch<React.SetStateAction<boolean>>
  activeTab: Tab
  setActiveTab: React.Dispatch<React.SetStateAction<Tab>>
  rowPosition: number
}

const closeIcon = (
  <svg width="17" height="17" viewBox="0 0 17 17" xmlns="http://www.w3.org/2000/svg">
    <title>Close icon</title>
    <path
      d="M15.577.808l1.23 1.23-6.77 6.77 6.77 6.769-1.23 1.23-6.77-6.77-6.769 6.77-1.23-1.23 6.769-6.77-6.77-6.769L2.039.808l6.77 6.77 6.769-6.77z"
      fill="#151515"
      fillRule="evenodd"
    />
  </svg>
)

const OfferDetailsModal: FC<Props> = ({ offer, visible, setVisible, activeTab, setActiveTab, rowPosition }) => {
  const calculatedLoanAmount = useStoreState(state => state.dimensions.calculatedLoanAmount)

  if (visible) {
    document.getElementsByTagName('html')[0].style.overflow = 'hidden'
    document.getElementsByTagName('body')[0].style.overflow = ''
  } else {
    document.getElementsByTagName('html')[0].style.overflow = ''
  }

  return (
    <Dialog
      visible={visible}
      onClose={() => setVisible(false)}
      wrapClassName={'center'}
      animation="zoom"
      focusTriggerAfterClose={false}
      closeIcon={closeIcon}
      footer={[
        <div key="footer" className="display-flex flex-row align-items-center">
          <div className="w-auto pr-5">
            <p className="mb-3 type-body-two text-slate">Rate</p>
            <span className="Numeral text-black">
              {offer.rate.toFixed(3)}
              <span className="Numeral-accent text-black">%</span>
            </span>
          </div>
          <div className="w-auto px-5">
            <p className="mb-3 type-body-two text-slate">APR ({offer.lockDays}-day rate lock)</p>
            <span className="Numeral text-black">
              {offer.apr.toFixed(3)}
              <span className="Numeral-accent text-black">%</span>
            </span>
          </div>
          <div className="w-auto px-5">
            <p className="mb-3 type-body-two text-slate">Loan amount</p>
            <span className="Numeral text-black">
              <span className="Numeral-accent text-black">$</span>
              {currency.formatNumeral(calculatedLoanAmount, 0)}
            </span>
          </div>
          <div className="w-auto px-5" data-testid="col-payment">
            <p className="mb-3 type-body-two text-slate">Monthly payment</p>
            <span className="Numeral text-black">
              <span className="Numeral-accent text-black">$</span>
              {currency.formatNumeral(offer.estimatedPayment, 0)}
            </span>
            <div className="type-body-three text-slate pos-absolute" style={{ height: '0px' }}>
              {offer.product.type !== 'adjustable' && `${offer.product.fixedMonths} equal monthly payments`}
              {offer.product.type === 'adjustable' && ``}
            </div>
          </div>
          <div className="get-offers px-2">
            {offer.advertiser.phone && (
              <>
                <span className="Icon Icon--sm">
                  <PhoneIcon />
                </span>
                <span className="Numeral text-black font-circular-bold type-body-one">{offer.advertiser.phone}</span>
              </>
            )}
            <CTAButton offer={offer} rowPosition={rowPosition} clickType={8} className="Button Button--primary ml-4" />
          </div>
        </div>,
      ]}
    >
      <div className="offer-details__mask"></div>
      <div className="modal-tabs" data-tabs="tabs-container">
        <div
          className="tabs__modal-header display-flex z-layer-two pos-sticky bg-white"
          style={{
            top: '0',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
          }}
        >
          <div className="offer-details-logo">
            <img
              style={{ maxHeight: '45px', maxWidth: '160px' }}
              alt={`${offer.advertiser.name} logo`}
              src={`//www.brimg.net/system/img/inst/${offer.advertiser.id}_hires_logo_2x.png?optimize=high&auto=webp`}
            />
          </div>
          <ul
            className="Tabs mb-0 border-0 justify-content-center"
            data-tabs="tab-list"
            role="tablist"
            aria-label="tabs"
          >
            <li className="Tabs-item" role="presentation">
              <button
                className={`Tabs-link ${activeTab === Tab.Details ? 'is-active' : ''}`}
                data-tabs="tab"
                data-testid="detailsTabButton"
                type="button"
                role="tab"
                aria-selected="true"
                onClick={() => setActiveTab(Tab.Details)}
              >
                Details
              </button>
            </li>
            <li className="Tabs-item" role="presentation">
              <button
                className={`Tabs-link ${activeTab === 'payment' ? 'is-active' : ''}`}
                data-tabs="tab"
                data-testid="paymentTabButton"
                type="button"
                role="tab"
                aria-selected="false"
                onClick={() => setActiveTab(Tab.Payment)}
              >
                Payment
              </button>
            </li>
            <li className="Tabs-item" role="presentation">
              <button
                className={`Tabs-link ${activeTab === 'reviews' ? 'is-active' : ''}`}
                data-tabs="tab"
                type="button"
                data-testid="reviewsTabButton"
                role="tab"
                aria-selected="false"
                onClick={() => setActiveTab(Tab.Reviews)}
              >
                Reviews
              </button>
            </li>
          </ul>
        </div>
        <div
          className="Tabs-panel pt-8 px-6"
          data-tabs="tab-panel"
          role="tabpanel"
          data-testid="detailsTab"
          aria-hidden={activeTab !== Tab.Details}
        >
          <Suspense fallback={<h4 className="type-heading-seven">Details</h4>}>
            <DetailsTab offer={offer} />
          </Suspense>
        </div>
        <div
          className="Tabs-panel pt-8 px-6"
          data-tabs="tab-panel"
          role="tabpanel"
          data-testid="paymentTab"
          aria-hidden={activeTab !== Tab.Payment}
        >
          <Suspense fallback={<h4 className="type-heading-seven">Calculate your monthly payment</h4>}>
            <PaymentTab offer={offer} />
          </Suspense>
        </div>
        <div
          className="Tabs-panel pt-8 px-6"
          data-tabs="tab-panel"
          role="tabpanel"
          aria-hidden={activeTab !== Tab.Reviews}
        >
          <Suspense fallback={<h4 className="type-heading-seven">Consumer Reviews</h4>}>
            <ReviewsTab offer={offer} />
          </Suspense>
        </div>
      </div>
    </Dialog>
  )
}

export default OfferDetailsModal
